import React, { useState } from 'react';
import { ArrowRight, Cloud, Database, Code, Terminal, PieChart, Lock, Grid } from 'lucide-react';
import { Link } from 'react-router-dom';

const HomePage = () => {
  const [activeProject, setActiveProject] = useState(0);
  
  const projects = [
    {
      title: "Cloud Automation at Syntasa",
      description: "Built out terraform automation and deployment modules across AWS and GCP platforms, enhancing Spark workloads with Kubernetes support.",
      technologies: ["AWS", "GCP", "Terraform", "Kubernetes", "Spark", "Hadoop"],
      icon: <Cloud className="text-blue-500" />
    },
    {
      title: "GCP Oklahoma Modernization",
      description: "Designed GCP data warehouse infrastructure with hub-and-spoke model for automated Airflow deployments.",
      technologies: ["GCP", "Terraform", "BigQuery", "Dataproc", "GKE", "Airflow"],
      icon: <Database className="text-green-500" />
    },
    {
      title: "AWS SaaS Migration",
      description: "Migrated from DigitalOcean PaaS to AWS SaaS with serverless architecture and React frontend.",
      technologies: ["AWS Lambda", "API Gateway", "CloudFront", "S3", "Cognito", "React"],
      icon: <Code className="text-orange-500" />
    },
    {
      title: "Data Visualization Improvements",
      description: "Enhanced pipeline maturity, refactored permission systems, and standardized API code structure.",
      technologies: ["DevOps", "API Design", "Security", "Testing", "Integration"],
      icon: <PieChart className="text-purple-500" />
    }
  ];

  const skills = [
    { name: "Cloud Platforms", icon: <Cloud />, items: ["AWS", "GCP", "Azure"] },
    { name: "Infrastructure as Code", icon: <Terminal />, items: ["Terraform", "CloudFormation", "Cloud CDK"] },
    { name: "Data Engineering", icon: <Database />, items: ["Spark", "Hadoop", "Airflow", "Data Warehousing"] },
    { name: "DevOps & CI/CD", icon: <Grid />, items: ["Kubernetes", "Openshift", "Docker", "CodePipeline", "CloudBuild", "Gitlab", "Jenkins"] },
    { name: "Security", icon: <Lock />, items: ["IAM", "Key Management", "Security Scanning"] }
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-teal-500 text-white">
        <div className="container mx-auto px-6 py-20">
          <h1 className="text-5xl font-bold mb-4">Platforms Engineer</h1>
          <h2 className="text-3xl font-semibold mb-8">Specializing in Big Data Infrastructure</h2>
          <p className="text-xl max-w-2xl mb-8">
            Building scalable, resilient cloud solutions on AWS and GCP with a focus on 
            infrastructure automation, data engineering, and security best practices.
          </p>
          <Link to="/projects" className="bg-white text-blue-600 font-semibold px-6 py-3 rounded-lg 
            shadow-lg hover:bg-blue-50 transition duration-300 flex items-center inline-flex">
            View My Work <ArrowRight className="ml-2" size={18} />
          </Link>
        </div>
      </div>

      {/* Skills Overview */}
      <div className="container mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold mb-12 text-center">Technical Expertise</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {skills.map((skill, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition duration-300">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-blue-100 rounded-full mr-4">
                  {skill.icon}
                </div>
                <h3 className="text-xl font-semibold">{skill.name}</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {skill.items.map((item, i) => (
                  <span key={i} className="bg-gray-100 px-3 py-1 rounded-full text-sm">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Featured Projects */}
      <div className="bg-gray-100 py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 text-center">Featured Projects</h2>
          
          <div className="flex overflow-x-auto pb-4 mb-8 scrollbar-hide">
            {projects.map((project, index) => (
              <button
                key={index}
                className={`px-5 py-3 rounded-lg mr-4 whitespace-nowrap flex-shrink-0 transition duration-300 ${
                  activeProject === index 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setActiveProject(index)}
              >
                {project.title}
              </button>
            ))}
          </div>
          
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-gray-100 rounded-full mr-4">
                  {projects[activeProject].icon}
                </div>
                <h3 className="text-2xl font-bold">{projects[activeProject].title}</h3>
              </div>
              <p className="text-lg mb-6">{projects[activeProject].description}</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {projects[activeProject].technologies.map((tech, i) => (
                  <span key={i} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                    {tech}
                  </span>
                ))}
              </div>
              <Link 
                to={`/projects/${activeProject === 0 ? 'cloud-automation' : 
                                activeProject === 1 ? 'gcp-oklahoma' : 
                                activeProject === 2 ? 'aws-migration' : 
                                'data-visualization'}`}
                className="text-blue-600 font-semibold flex items-center hover:text-blue-700 transition duration-300"
                >
                View Case Study <ArrowRight className="ml-2" size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="container mx-auto px-6 py-20 text-center">
        <h2 className="text-3xl font-bold mb-4">Interested in working together?</h2>
        <p className="max-w-2xl mx-auto text-lg mb-8">
          I'm currently available for freelance projects or contract work.
        </p>
        <div className="flex justify-center space-x-4">
        <Link to="/contact" className="bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg shadow-lg hover:bg-blue-700 transition duration-300">
            Contact Me
        </Link>
        <a href="/Philip_Crilley_Resume.pdf" target="_blank" className="bg-white border border-gray-300 text-gray-700 font-semibold px-6 py-3 rounded-lg shadow-lg hover:bg-gray-50 transition duration-300">
            Download Resume
        </a>
        </div>
      </div>
    </div>
  );
};

export default HomePage;